import { IdSchemaEnum } from "../../../Configurator/Enums/IdSchemaEnum";
import SDKConfigurationModel from "../../../Configurator/Models/SDKConfiguraitonModel";
import MainFiltersBQ from "../../../Global/Models/Filters/MainFiltersBQ";
import VotingHttps from "../../../Https/VotingHttps";
import PotmUserVotesFilters from "../Models/PlayerOfTheMatch/Filters/PotmUserVotesFilters";
import PollsFilters from "../Models/Polls/Filters/PollsFilters";
import VotingService from "../Service/VotingService";

export default class VotingFacade {
  private https: VotingHttps = null;
  private service: VotingService = null;
  private idSchema: string = null;

  constructor(config: SDKConfigurationModel) {
    this.idSchema = config.idSchema;
    this.https = new VotingHttps(config);
    this.service = new VotingService(config);
  }

  public getPolls = async (filters?: PollsFilters, disableCache?: boolean) => {
    if (filters) {
      filters = new PollsFilters(filters);
      await this.service.finalizePollsFilters(filters);
    }

    const response = await this.https.getPolls(filters, disableCache);

    for (let value of response.data) {
      await this.service.remapTags(value, IdSchemaEnum.NATIVE, this.idSchema);
    }

    return response;
  };

  public getPollById = async (pollId: string, disableCache?: boolean) => {
    const data = await this.https.getPollById(pollId, disableCache);
    await this.service.remapTags(data, IdSchemaEnum.NATIVE, this.idSchema);

    return data;
  };

  public voteForPoll = async (pollId: string, optionId: string | string[]) => {
    if (typeof optionId === "string") {
      console.warn(
        "Passing a single optionId as a string will be deprecated. Please pass it as an array."
      );
      optionId = [optionId];
    }

    return await this.https.voteForPoll(pollId, optionId);
  };

  public deleteVoteForPoll = async (pollId: string) => {
    return await this.https.deleteVoteForPoll(pollId);
  };

  public getMyPollVote = async (pollId: string, showModels?: boolean) => {
    const data = await this.https.getMyPollVote(pollId);

    if (showModels) {
      const polls = await this.getPolls(
        new PollsFilters({ pollIds: [pollId] }),
        true
      );
      data.pollModel = polls.data.find((poll) => poll.id === pollId) || null;
      data.optionModel =
        polls.data
          .find((poll) => poll.id === pollId)
          ?.options.find((option) => option.id === data.optionId) || null;
      data.optionsModels = data.optionIds
        ? polls.data
            .find((poll) => poll.id === pollId)
            ?.options.filter((option) => data.optionIds.includes(option.id)) ||
          []
        : [];
      data.previousVotes.forEach((pv) => {
        pv.optionsModels =
          polls.data
            .find((poll) => poll.id === pv.pollId)
            ?.options.filter((option) => pv.optionIds.includes(option.id)) ||
          [];
      });
    }

    return data;
  };

  public getMyPollsVotes = async (filters?: MainFiltersBQ) => {
    if (filters) {
      filters = new MainFiltersBQ(filters);
    }

    const data = await this.https.getMyPollsVotes(filters);
    const pollIds = data.data.map((poll) => poll.id);
    const polls = await this.getPolls(new PollsFilters({ pollIds }), true);
    data.data = data.data.map((value) => {
      value.pollModel =
        polls.data.find((poll) => poll.id === value.pollId) || null;
      value.optionModel =
        value.pollModel?.options.find(
          (option) => option.id === value.optionId
        ) || null;
      value.optionsModels = value.optionIds
        ? value.pollModel?.options.filter((option) =>
            value.optionIds.includes(option.id)
          ) || []
        : [];

      return value;
    });

    return data;
  };

  public voteForPlayerOfTheMatch = async (
    matchId: string,
    playerId: string
  ) => {
    if (this.idSchema !== IdSchemaEnum.NATIVE) {
      const [nativeMatchId, nativePlayerId] =
        await this.service.remapMatchAndPlayerIds(
          matchId,
          playerId,
          this.idSchema,
          IdSchemaEnum.NATIVE
        );
      await this.https.voteForPlayerOfTheMatch(nativeMatchId, nativePlayerId);

      return { playerId };
    }

    return await this.https.voteForPlayerOfTheMatch(matchId, playerId);
  };

  public deleteVoteForPlayerOfTheMatch = async (matchId: string) => {
    if (this.idSchema !== IdSchemaEnum.NATIVE) {
      const nativeMatchIds = await this.service.remapMatchIds(
        [matchId],
        this.idSchema,
        IdSchemaEnum.NATIVE
      );
      await this.https.deleteVoteForPlayerOfTheMatch(nativeMatchIds[0]);
      return true;
    }

    await this.https.deleteVoteForPlayerOfTheMatch(matchId);

    return true;
  };

  public getOwnVotesForPlayerOfTheMatch = async (
    filters?: PotmUserVotesFilters
  ) => {
    if (filters) {
      filters = new PotmUserVotesFilters(filters);
    }

    if (
      filters &&
      filters.matchIds &&
      filters.matchIds.length > 0 &&
      this.idSchema !== IdSchemaEnum.NATIVE
    ) {
      const nativeMatchIds = await this.service.remapMatchIds(
        filters.matchIds,
        this.idSchema,
        IdSchemaEnum.NATIVE
      );
      filters.matchIds = nativeMatchIds;
    }

    const response = await this.https.getOwnVotesForPlayerOfTheMatch(filters);

    return await this.service.setModelsForUserVotes(response);
  };

  public getResultsForPlayerOfTheMatch = async (
    matchId: string,
    disableCache?: boolean
  ) => {
    if (this.idSchema !== IdSchemaEnum.NATIVE) {
      const nativeMatchIds = await this.service.remapMatchIds(
        [matchId],
        this.idSchema,
        IdSchemaEnum.NATIVE
      );
      const response = await this.https.getResultsForPlayerOfTheMatch(
        nativeMatchIds[0],
        disableCache
      );
      await this.service.setModelsForPotmResults(response.data);
      return response;
    }
    const response = await this.https.getResultsForPlayerOfTheMatch(
      matchId,
      disableCache
    );

    await this.service.setModelsForPotmResults(response.data);

    return response;
  };
}
